import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, CheckCircle2, Mail } from "lucide-react";

function strengthOf(pw) {
  let s = 0;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s; // 0..4
}

const STRENGTH_LABELS = ["Très faible", "Faible", "Moyen", "Bon", "Excellent"];
const STRENGTH_COLORS = ["bg-red-500", "bg-orange-500", "bg-amber-500", "bg-lime-500", "bg-emerald-600"];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  const s = strengthOf(pw);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 12) { toast.error("Mot de passe trop court (minimum 12 caractères)"); return; }
    if (pw !== pw2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Mot de passe réinitialisé");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const resendEmail = async () => {
    setResendLoading(true);
    try {
      const { data } = await api.post("/auth/resend-reset", { token });
      if (data.email_sent) {
        toast.success("Nouveau mail envoyé");
        setResendDone(true);
      } else if (data.dev_reset_link) {
        toast.info("Email indisponible — lien direct affiché");
        window.location.href = data.dev_reset_link;
      } else {
        toast.success(data.message);
        setResendDone(true);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setResendLoading(false); }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center">
        <h2 className="font-display font-black text-2xl mb-3">Lien invalide</h2>
        <p className="text-sm text-neutral-600 mb-6">Le lien que vous avez utilisé n'est pas valide.</p>
        <Link to="/forgot-password">
          <Button className="rounded-full bg-[#1F4032] hover:bg-[#2A5443]">Demander un nouveau lien</Button>
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="reset-done">
        <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-600" />
        <h2 className="font-display font-black text-2xl mb-2">Mot de passe modifié</h2>
        <p className="text-sm text-neutral-600 mb-6">Redirection vers la connexion…</p>
        <Link to="/login">
          <Button className="rounded-full bg-[#1F4032] hover:bg-[#2A5443]">Se connecter</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="reset-page">
      <div className="overline mb-2">— Réinitialisation</div>
      <h1 className="font-display font-black text-3xl tracking-tighter mb-2">Nouveau mot de passe</h1>
      <p className="text-sm text-neutral-600 mb-8">Choisissez un mot de passe robuste. Il remplacera l'ancien immédiatement.</p>

      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <Label className="overline">Nouveau mot de passe</Label>
          <div className="relative mt-2">
            <Input type={show ? "text" : "password"} required minLength={12}
              value={pw} onChange={(e) => setPw(e.target.value)}
              className="rounded-md h-11 pr-11" data-testid="reset-pw1" />
            <button type="button" onClick={() => setShow(!show)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-black">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {pw && (
            <div className="mt-2 space-y-1" data-testid="strength-bar">
              <div className="flex gap-1">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={`h-1 flex-1 rounded ${i < s ? STRENGTH_COLORS[s] : "bg-neutral-200"}`} />
                ))}
              </div>
              <div className="text-[11px] text-neutral-500 font-mono">
                Force : {STRENGTH_LABELS[s]} · 12+ caractères, majuscule, chiffre, symbole
              </div>
            </div>
          )}
        </div>
        <div>
          <Label className="overline">Confirmer</Label>
          <div className="relative mt-2">
            <Input type={show ? "text" : "password"} required value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="rounded-md h-11 pr-11" data-testid="reset-pw2" />
          </div>
          {pw2 && pw !== pw2 && <p className="text-xs text-red-600 mt-1">Les mots de passe ne correspondent pas</p>}
        </div>
        <Button type="submit" disabled={loading || !pw || pw !== pw2}
          className="w-full rounded-full bg-[#1F4032] hover:bg-[#2A5443] h-12 gap-2"
          data-testid="reset-submit">
          <KeyRound className="w-4 h-4" /> {loading ? "Modification…" : "Modifier mon mot de passe"}
        </Button>

        <div className="pt-4 border-t border-neutral-200">
          <p className="text-xs text-neutral-500 mb-2 text-center">
            Un problème avec ce lien ? Demandez un nouvel email.
          </p>
          <Button type="button" variant="outline"
            onClick={resendEmail} disabled={resendLoading || resendDone}
            className="w-full rounded-full border-neutral-300 gap-2"
            data-testid="resend-email-btn">
            <Mail className="w-4 h-4" />
            {resendLoading ? "Envoi…" : resendDone ? "✓ Nouveau mail envoyé" : "Renvoyer un mail"}
          </Button>
        </div>

        <Link to="/login" className="block text-center text-sm text-neutral-600 hover:text-black underline">
          Annuler
        </Link>
      </form>
    </div>
  );
}
